import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Space,
  Avatar,
  Divider,
  Timeline,
  Progress,
  Statistic,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  List,
  message,
  Spin,
  Tabs,
  Empty,
  InputNumber,
  Alert,
  App,
  Tooltip,
  Descriptions,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  FileTextOutlined,
  PlusOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  DownloadOutlined,
  FileOutlined,
  DeleteOutlined,
  WhatsAppOutlined,
  SendOutlined,
  PhoneFilled,
  AudioOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  CustomerServiceOutlined,
  ClockCircleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useLeadStore from "../../store/leadStore";
import useAuthStore from "../../store/authStore";
import TasksList from "../TasksList";
import apiService from "../../services/apiService";
import dayjs from "dayjs";
import { v4 as uuidv4 } from 'uuid'; // Import uuid for local message IDs
import callLogService from '../../services/callLogService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Check if WhatsApp feature is enabled via environment variable
const isWhatsAppEnabled = process.env.REACT_APP_WHATSAPP_ENABLED === 'true';

// Check if AI Calling feature is enabled via environment variable
const isAICallingEnabled = process.env.REACT_APP_AI_CALLING_ENABLED === 'true';

// Default WhatsApp Channel ID fallback when lead.channelId is missing
const DEFAULT_WHATSAPP_CHANNEL_ID = process.env.REACT_APP_DEFAULT_WHATSAPP_CHANNEL_ID || '';
const DEFAULT_FROM_NUMBER = process.env.REACT_APP_PLIVO_SOURCE_NUMBER || process.env.REACT_APP_PLIVO_CALLER_ID || '';

const LeadDetailView = () => {
  const { message: messageApi } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    currentLead,
    fetchLeadById,
    updateLead,
    addNote,
    updateNote,
    deleteNote,
    addActivity,
    addRequirement,
    updateRequirement,
    deleteRequirement,
    addMeeting,
    updateMeeting,
    deleteMeeting,
    addCall,
    updateCall,
    deleteCall,
    addDocument,
    addTask,
    updateTask,
    deleteTask,
    uploadDocument,
    deleteDocument,
    convertToDeal,
  } = useLeadStore();

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [activityModalVisible, setActivityModalVisible] = useState(false);
  const [requirementModalVisible, setRequirementModalVisible] = useState(false);
  const [meetingModalVisible, setMeetingModalVisible] = useState(false);
  const [callModalVisible, setCallModalVisible] = useState(false);
  const [convertToDealModalVisible, setConvertToDealModalVisible] =
    useState(false);
  const [assignLeadModalVisible, setAssignLeadModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [activeTab, setActiveTab] = useState("requirements");
  const [activityType, setActivityType] = useState("note");
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [taskForm] = Form.useForm();
  const [noteForm] = Form.useForm();
  const [documentForm] = Form.useForm();
  const [convertForm] = Form.useForm();
  const [activityForm] = Form.useForm();
  const [requirementForm] = Form.useForm();
  const [meetingForm] = Form.useForm();
  const [callForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const isConverted = currentLead?.convertedToDeal;

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const [isCalling, setIsCalling] = useState(false); // New state for AI calling
  const [transcripts, setTranscripts] = useState([]); // New state for transcripts
  const [isTranscriptsLoading, setIsTranscriptsLoading] = useState(false); // New state for transcripts loading
  const [uploadingFiles, setUploadingFiles] = useState([]); // Track uploading files
  const [whatsappUnavailable, setWhatsappUnavailable] = useState(false);
  const [manualCallModalVisible, setManualCallModalVisible] = useState(false);
  const [manualCallForm] = Form.useForm();
  const [callLogs, setCallLogs] = useState([]);
  const [isLoadingCallLogs, setIsLoadingCallLogs] = useState(false);

  // Fetch call logs when tab changes to 'calllogs'
  useEffect(() => {
    const fetchCallLogs = async () => {
      if (activeTab === 'calllogs' && currentLead?.phone) {
        setIsLoadingCallLogs(true);
        try {
          const response = await callLogService.getCallLogs(currentLead.phone);
          if (response.success) {
            setCallLogs(response.data);
          } else {
            messageApi.error('Failed to fetch call logs');
          }
        } catch (error) {
          console.error('Error fetching call logs:', error);
          messageApi.error('Failed to fetch call logs');
        } finally {
          setIsLoadingCallLogs(false);
        }
      }
    };
    fetchCallLogs();
  }, [activeTab, currentLead?.phone]);

  useEffect(() => {
    const loadLeadData = async () => {
      if (id) {
        setIsLoading(true); // Set loading to true before fetching
        await fetchLeadById(id);
        loadTeamMembers();
        setIsLoading(false); // Set loading to false after fetching
      }
    };
    loadLeadData();
  }, [id, fetchLeadById]);

  // Initialize Plivo Browser SDK (WebRTC) once on app mount
  useEffect(() => {
    const sipUser = process.env.REACT_APP_PLIVO_SIP_USER;
    const sipPassword = process.env.REACT_APP_PLIVO_SIP_PASSWORD;

    if (!window.Plivo) {
      console.warn('[Plivo] Browser SDK not loaded yet.');
      return;
    }
    if (!sipUser || !sipPassword) {
      console.warn('[Plivo] SIP credentials not set in environment (REACT_APP_PLIVO_SIP_USER / REACT_APP_PLIVO_SIP_PASSWORD). Skipping login.');
      return;
    }

    try {
      const options = {
        debug: 'INFO',
        permOnClick: true,
        enableQualityTracking: 'all',
        closeProtection: true,
      };
      // Initialize Browser SDK v2
      const plivoBrowserSdk = new window.Plivo(options);

      // Register events on the client
      plivoBrowserSdk.client.on('onLogin', () => console.log('[Plivo] Logged in'));
      plivoBrowserSdk.client.on('onLoginFailed', (err) => console.error('[Plivo] Login failed:', err));
      plivoBrowserSdk.client.on('onLogout', () => console.log('[Plivo] Logged out'));

      plivoBrowserSdk.client.on('onIncomingCall', (callerID, extraHeaders, callInfo, callerName) => {
        console.log('[Plivo] Incoming call:', { callerID, callerName, callInfo, extraHeaders });
        try {
          // Answer the most recent ringing call or use callInfo.callUUID explicitly
          plivoBrowserSdk.client.answer(callInfo?.callUUID);
        } catch (e) {
          console.error('[Plivo] Failed to answer call:', e);
        }
      });
      plivoBrowserSdk.client.on('onCallAnswered', (callInfo) => {
        console.log('[Plivo] Call answered:', callInfo);
        messageApi.destroy(); // Clear the "Calling..." message
        messageApi.success('Call connected');
        setIsCalling(false);
      });
      
      plivoBrowserSdk.client.on('onCallConnected', (callInfo) => {
        console.log('[Plivo] Call connected:', callInfo);
        messageApi.destroy(); // Clear the "Calling..." message
        setIsCalling(false);
      });
      
      plivoBrowserSdk.client.on('onCallTerminated', (hangupInfo, callInfo) => {
        console.log('[Plivo] Call ended:', { hangupInfo, callInfo });
        // stopRingingAudio();
        messageApi.destroy(); // Clear any existing messages
        messageApi.info('Call ended');
        setIsCalling(false);
        setActiveCallUUID(null);
        // Ensure media streams are stopped
        window._plivo.client.hangup();
      });

      // Handle media failures (can happen when remote party hangs up)
      plivoBrowserSdk.client.on('onMediaError', (err) => {
        console.error('[Plivo] Media error:', err);
        // stopRingingAudio();
        messageApi.destroy();
        messageApi.error('Call disconnected due to media error');
        setIsCalling(false);
        setActiveCallUUID(null);
        window._plivo.client.hangup();
      });

      // Handle call failures
      plivoBrowserSdk.client.on('onCallFailed', (reason, callInfo) => {
        console.error('[Plivo] Call failed:', { reason, callInfo });
        // stopRingingAudio();
        messageApi.destroy();
        messageApi.error('Call failed: ' + reason);
        setIsCalling(false);
        setActiveCallUUID(null);
        window._plivo.client.hangup();
        // plivoBrowserSdk.client.disconnect();
      });
      
      // Handle remote disconnect/hangup specifically
      plivoBrowserSdk.client.on('onRemoteDisconnect', (callInfo) => {
        stopRingingAudio();
        console.log('[Plivo] Remote party disconnected:', callInfo);
        messageApi.destroy();
        messageApi.info('Other party ended the call');
        setIsCalling(false);
        setActiveCallUUID(null);
        window._plivo.client.hangup();
      });

      // Expose globally for debugging/usage in other components if needed
      window._plivo = plivoBrowserSdk;

      // Login using SIP Endpoint credentials
      plivoBrowserSdk.client.login(sipUser, sipPassword);
      console.log('[Plivo] Initializing and logging in as', sipUser);
    } catch (e) {
      console.error('[Plivo] Initialization error:', e);
    }
  }, []);

  

  const [activeCallUUID, setActiveCallUUID] = useState(null);

  // Stop ringing audio if present
  const stopRingingAudio = () => {
    const ringingAudio = document.getElementById('plivo-ringing-audio');
    if (ringingAudio) {
      ringingAudio.pause();
      ringingAudio.currentTime = 0;
    }
  };

  const handleEndCall = () => {
    stopRingingAudio();
    if (window._plivo?.client && activeCallUUID) {
      try {
        window._plivo.client.hangup();
        setActiveCallUUID(null);
        setIsCalling(false);
        messageApi.destroy(); // Clear any existing messages
        messageApi.info("Call ended");
      } catch (error) {
        console.error("Error ending call:", error);
        messageApi.error("Failed to end call");
      }
    }
  };


  const handleStartManualCall = async (values) => {
    const numberToCall = currentLead?.phone || values.phoneNumber;
    if (!numberToCall) { 
      messageApi.error("No phone number available to call.");
      return;
    }
    if (!window._plivo?.client) {
      messageApi.error("Plivo client not initialized.");
      console.error("Plivo client not initialized.");
      return;
    }
    try {
      setIsCalling(true);
      messageApi.open({ type: "loading", content: "Calling...", duration: 0 });

      // Format number to E.164
      const formattedNumber = numberToCall.startsWith('+') 
        ? numberToCall 
        : `+${numberToCall.replace(/\D/g, '')}`;

      // Make call
      const callUUID = window._plivo.client.call(formattedNumber);
      setActiveCallUUID(callUUID);

    } catch (error) {
      console.error("Error during browser call:", error);
      messageApi.error("Failed to initiate browser call.");
      messageApi.destroy();
      setIsCalling(false);
    }
  };

  const handleStartAICallCampaign = async () => {
    if (!currentLead?._id) {
      messageApi.error("Lead ID is missing. Cannot start AI call campaign.");
      return;
    }
    setIsCalling(true);
    try {
      const response = await apiService.post(`/leads/${currentLead._id}/start-aicall-campaign`);
      if (response.data.code === "AICALL_CAMPAIGN_STARTED") {
        messageApi.success(response.data.message);
        // Optionally, update lead status or add an activity for the call
        fetchLeadById(id);
      } else {
        messageApi.error(response.data.message || "Failed to start AI call campaign.");
      }
    } catch (error) {
      console.error("Error starting AI call campaign:", error);
      messageApi.error(error.response?.data?.message || "Failed to start AI call campaign.");
    } finally {
      setIsCalling(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Create a temporary anchor element to download the file
      const link = document.createElement('a');
      link.href = doc.url;
      link.target = '_blank';
      link.download = doc.name;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Downloading ${doc.name}`);
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download file');
    }
  };

  useEffect(() => {
    const hasChannel = currentLead?.channelId || DEFAULT_WHATSAPP_CHANNEL_ID;
    if (activeTab === 'whatsapp' && (hasChannel || currentLead?.phone)) {
      fetchChatMessages();
    } else if (activeTab === 'aicalling' && currentLead?.campaignIds?.length > 0) {
      fetchTranscripts();
    }
  }, [activeTab, currentLead?.channelId, currentLead?.phone, currentLead?.campaignIds]);

  // Auto-scroll to bottom when messages update and WhatsApp tab is active
  useEffect(() => {
    if (activeTab === 'whatsapp' && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, activeTab]);

  const fetchTranscripts = async () => {
    if (!currentLead?.campaignIds || currentLead.campaignIds.length === 0) {
      setTranscripts([]);
      return;
    }

    setIsTranscriptsLoading(true);
    try {
      const response = await apiService.post('/pitchbot/transcripts', {
        ids: currentLead.campaignIds,
      });
      if (response.status === 200) {
        setTranscripts(response.data);
      } else {
        messageApi.error(response.data.message || "Failed to fetch transcripts.");
      }
    } catch (error) {
      console.error("Error fetching transcripts:", error);
      messageApi.error(error.response?.data?.message || "Failed to fetch transcripts.");
    } finally {
      setIsTranscriptsLoading(false);
    }
  };

  const fetchChatMessages = async () => {
    const resolvedChannelId = currentLead?.channelId || DEFAULT_WHATSAPP_CHANNEL_ID;
    if (!resolvedChannelId && !currentLead?.phone) {
      messageApi.warning("Lead does not have a channel ID or phone number for WhatsApp chat.");
      return;
    }
    setIsChatLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (resolvedChannelId) {
        queryParams.append('channelId', resolvedChannelId);
      }
      if (currentLead.phone) {
        queryParams.append('phoneNumber', currentLead.phone);
      }
      const response = await apiService.get(`/chat/messages?${queryParams.toString()}`);
      if (response.data.success) {
        setWhatsappUnavailable(false);
        setChatMessages(response.data.data.messages);
      } else {
        if (response.data.code === "WHATSAPP_CONFIG_MISSING" || response.data.code === "WHATSAPP_PROVIDER_UNAVAILABLE") {
          setWhatsappUnavailable(true);
        }
        messageApi.error(response.data.message || "Failed to fetch chat messages.");
      }
    } catch (error) {
      console.error("Failed to fetch chat messages:", error);
      const serverCode = error?.response?.data?.code;
      if (serverCode === "WHATSAPP_CONFIG_MISSING" || serverCode === "WHATSAPP_PROVIDER_UNAVAILABLE") {
        setWhatsappUnavailable(true);
        messageApi.error(error.response?.data?.message || "WhatsApp integration is unavailable.");
      } else {
        messageApi.error("Failed to fetch chat messages.");
      }
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleSendChatMessage = async () => {
    if (!chatInput.trim()) {
      return;
    }
    const resolvedChannelId = currentLead?.channelId || DEFAULT_WHATSAPP_CHANNEL_ID;
    if (!resolvedChannelId || !currentLead?.phone) {
      messageApi.error("Cannot send message: Missing channel ID or recipient phone.");
      return;
    }
    if (whatsappUnavailable) {
      messageApi.error("WhatsApp integration is currently unavailable.");
      return;
    }

    const newMessage = {
      _id: uuidv4(), // Temporary ID for optimistic update
      entity: 'bot', // Assuming messages sent from CRM are bot/agent responses
      message: chatInput,
      timestamp: new Date().toISOString(),
      channelId: resolvedChannelId,
      contactId: currentLead.contactId, // Assuming contactId is available on currentLead
      recipientPhone: currentLead.phone,
      conversationId: currentLead.conversationId, // Assuming conversationId is available
      accountId: currentLead.accountId, // Assuming accountId is available
      contactName: `${currentLead.firstName} ${currentLead.lastName}`,
      channelNumber: currentLead.channelNumber, // Assuming channelNumber is available
      whatsappType: 'text',
    };

    // Optimistic update
    setChatMessages((prevMessages) => [...prevMessages, newMessage]);
    setChatInput('');

    try {
      const response = await apiService.post('/chat/whatsapp/send', {
        channelId: resolvedChannelId,
        contactId: currentLead.contactId,
        recipientPhone: currentLead.phone,
        message: newMessage.message,
        conversationId: currentLead.conversationId,
        accountId: currentLead.accountId,
        contactName: `${currentLead.firstName} ${currentLead.lastName}`,
        channelNumber: currentLead.channelNumber,
      });

      if (response.data.success) {
        messageApi.success("Message sent successfully!");
        // Replace optimistic message with actual message from backend if needed, or just refetch
        fetchChatMessages();
      } else {
        if (response.data.code === "WHATSAPP_CONFIG_MISSING" || response.data.code === "WHATSAPP_PROVIDER_UNAVAILABLE") {
          setWhatsappUnavailable(true);
        }
        messageApi.error(response.data.message || "Failed to send message.");
        // Revert optimistic update on error
        setChatMessages((prevMessages) => prevMessages.filter(msg => msg._id !== newMessage._id));
      }
    } catch (error) {
      // Extract detailed error info from server if available
      const serverMessage = error?.response?.data?.message;
      const upstreamStatus = error?.response?.data?.upstreamStatus;
      const serverCode = error?.response?.data?.code;
      const baseMsg = serverMessage || error?.message || "Failed to send message.";
      console.error("Error sending chat message:", {
        error,
        serverMessage,
        upstreamStatus,
        response: error?.response?.data,
      });
      if (serverCode === "WHATSAPP_CONFIG_MISSING" || serverCode === "WHATSAPP_PROVIDER_UNAVAILABLE") {
        setWhatsappUnavailable(true);
      }
      messageApi.error(baseMsg);
      // Revert optimistic update on error
      setChatMessages((prevMessages) => prevMessages.filter((msg) => msg._id !== newMessage._id));
    }
  };

  const loadTeamMembers = async () => {
    setLoadingTeamMembers(true);
    try {
      const response = await apiService.get("/users/team-members");

      if (response.data.success) {
        setTeamMembers(response.data.data.teamMembers);
      }
    } catch (error) {
      console.error("Failed to load team members:", error);
      if (error.response) {
        console.error(
          "Error response:",
          error.response.status,
          error.response.data
        );
      }
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const handleAddNote = async (values) => {
    const result = await addNote(id, values.content);
    if (result.success) {
      message.success("Note added successfully");
      setNoteModalVisible(false);
      noteForm.resetFields();
      await fetchLeadById(id); // Refresh data
    } else {
      message.error(result.error || "Failed to add note");
    }
  };

  const handleAddActivity = async (values) => {
    const result = await addActivity(id, {
      type: activityType,
      description: values.description,
      date: new Date(),
    });
    if (result.success) {
      message.success("Activity added successfully");
      setActivityModalVisible(false);
      activityForm.resetFields();
    } else {
      message.error(result.error || "Failed to add activity");
    }
  };

  const handleAddRequirement = async (values) => {
    const result = await addRequirement(id, values);
    if (result.success) {
      message.success("Requirement added successfully");
      setRequirementModalVisible(false);
      setEditingItem(null);
      requirementForm.resetFields();
    } else {
      message.error(result.error || "Failed to add requirement");
    }
  };

  const handleUpdateRequirement = async (values) => {
    const result = await updateRequirement(id, editingItem._id, values);
    if (result.success) {
      setRequirementModalVisible(false);
      requirementForm.resetFields();
      setEditingItem(null);
      message.success("Requirement updated successfully");
    } else {
      message.error(result.error || "Failed to update requirement");
    }
  };

  const handleDeleteRequirement = async (requirementId) => {
    const result = await deleteRequirement(id, requirementId);
    if (result.success) {
      message.success("Requirement deleted successfully");
    } else {
      message.error(result.error || "Failed to delete requirement");
    }
  };

  const handleAddMeeting = async (values) => {
    const result = await addMeeting(id, values);
    if (result.success) {
      message.success("Meeting scheduled successfully");
      setMeetingModalVisible(false);
      setEditingItem(null);
      meetingForm.resetFields();
      setEditingItem(null);
    } else {
      message.error(result.error || "Failed to add meeting");
    }
  };

  const handleUpdateMeeting = async (values) => {
    const result = await updateMeeting(id, editingItem._id, values);
    if (result.success) {
      setMeetingModalVisible(false);
      meetingForm.resetFields();
      setEditingItem(null);
      message.success("Meeting updated successfully");
    } else {
      message.error(result.error || "Failed to update meeting");
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    const result = await deleteMeeting(id, meetingId);
    if (result.success) {
      message.success("Meeting deleted successfully");
    } else {
      message.error(result.error || "Failed to delete meeting");
    }
  };

  const handleAddCall = async (values) => {
    try {
      // Format the data for the API
      const callData = {
        type: values.type,
        subject: values.subject,
        notes: values.notes,
        duration: values.duration || 0,
        outcome: values.outcome || "successful",
      };

      // Only include scheduledAt if user provided it
      if (values.scheduledAt) {
        callData.scheduledAt = values.scheduledAt.toISOString();
      }

      const result = await addCall(id, callData);

      if (result.success) {
        setCallModalVisible(false);
        callForm.resetFields();
        setEditingItem(null);
        messageApi.success("Call logged successfully");
        // Refresh the lead data to show the new call
        await fetchLeadById(id);
      } else {
        messageApi.error(result.error || "Failed to log call");
      }
    } catch (error) {
      console.error("Error in handleAddCall:", error);
      messageApi.error("Failed to log call");
    }
  };

  const handleUpdateCall = async (values) => {
    try {
      const callData = {
        type: values.type,
        subject: values.subject,
        notes: values.notes,
        duration: values.duration || 0,
        outcome: values.outcome || "successful",
      };

      // Only include scheduledAt if provided or already exists
      if (values.scheduledAt) {
        callData.scheduledAt = values.scheduledAt.toISOString();
      } else if (editingItem.scheduledAt) {
        callData.scheduledAt = editingItem.scheduledAt;
      }

      const result = await updateCall(id, editingItem._id, callData);

      if (result.success) {
        setCallModalVisible(false);
        callForm.resetFields();
        setEditingItem(null);
        messageApi.success("Call updated successfully");
        await fetchLeadById(id);
      } else {
        messageApi.error(result.error || "Failed to update call");
      }
    } catch (error) {
      console.error("Error in handleUpdateCall:", error);
      messageApi.error("Failed to update call");
    }
  };

  const handleDeleteCall = async (callId) => {
    try {
      const result = await deleteCall(id, callId);
      if (result.success) {
        messageApi.success("Call deleted successfully");
        await fetchLeadById(id);
      } else {
        messageApi.error(result.error || "Failed to delete call");
      }
    } catch (error) {
      console.error("Error in handleDeleteCall:", error);
      messageApi.error("Failed to delete call");
    }
  };

  const handleAddDocument = async (values) => {
    const result = await addDocument(id, values);
    if (result.success) {
      setDocumentModalVisible(false);
      message.success("Document added successfully");
    } else {
      message.error(result.error || "Failed to add document");
    }
  };

  const handleDeleteDocument = async (documentId) => {
    const result = await deleteDocument(id, documentId);
    if (result.success) {
      message.success("Document deleted successfully");
    } else {
      message.error(result.error || "Failed to delete document");
    }
  };

  const handleEditItem = (type, item) => {
    if (type === "note") {
      setEditingItem({ type: "note", item });
      noteForm.setFieldsValue({ content: item.content });
      setNoteModalVisible(true);
      return;
    }

    setEditingItem(item);
    if (type === "requirement") {
      requirementForm.setFieldsValue(item);
      setRequirementModalVisible(true);
    } else if (type === "meeting") {
      meetingForm.setFieldsValue({
        ...item,
        scheduledAt: item.scheduledAt ? dayjs(item.scheduledAt) : null,
      });
      setMeetingModalVisible(true);
    } else if (type === "call") {
      callForm.setFieldsValue({
        ...item,
        scheduledAt: item.scheduledAt ? dayjs(item.scheduledAt) : null,
      });
      setCallModalVisible(true);
    }
  };

  const handleDeleteItem = (type, itemId) => {
    Modal.confirm({
      title: `Delete ${type}?`,
      content: `Are you sure you want to delete this ${type}?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        if (type === "requirement") {
          handleDeleteRequirement(itemId);
        } else if (type === "meeting") {
          handleDeleteMeeting(itemId);
        } else if (type === "call") {
          handleDeleteCall(itemId);
        } else if (type === "document") {
          handleDeleteDocument(itemId);
        } else if (type === "note") {
          try {
            const result = await deleteNote(id, itemId);
            if (result.success) {
              message.success('Note deleted successfully');
              await fetchLeadById(id); // Refresh lead data
            } else {
              message.error(result.error || 'Failed to delete note');
            }
          } catch (error) {
            message.error('Failed to delete note');
            console.error('Error deleting note:', error);
          }
        }
      },
    });
  };

  const handleUpdateNote = async (values) => {
    if (editingItem?.type === "note") {
      const result = await updateNote(id, editingItem.item._id, values.content);
      if (result.success) {
        message.success("Note updated successfully");
        setNoteModalVisible(false);
        setEditingItem(null);
        noteForm.resetFields();
        await fetchLeadById(id);
      } else {
        message.error(result.error || "Failed to update note");
      }
    } else {
      handleAddNote(values);
    }
  };

  const handleConvertToDeal = async (values) => {
    // Transform the form values to match backend expectations
    const result = await convertToDeal(id, values);
    if (result.success) {
      message.success("Lead converted to deal successfully");
      setConvertToDealModalVisible(false);
      convertForm.resetFields();
      navigate("/deals");
    } else {
      message.error(result.error || "Failed to convert lead to deal");
    }
  };

  const handleAssignLead = async (values) => {
    try {
      const result = await updateLead(id, { assignedTo: values.assignedTo });
      if (result.success) {
        message.success("Lead assignment updated successfully");
        setAssignLeadModalVisible(false);
        assignForm.resetFields();
        fetchLeadById(id); // Refresh lead data
      } else {
        message.error(result.error || "Failed to update lead assignment");
      }
    } catch (error) {
      message.error("Failed to update lead assignment");
    }
  };

  const handleAddTask = () => {
    setEditingTask(null);
    taskForm.resetFields();
    setTaskModalVisible(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    taskForm.setFieldsValue({
      title: task.title,
      description: task.description,
      priority: task.priority,
      status: task.status,
      dueDate: task.dueDate ? dayjs(task.dueDate) : null,
      notes: task.notes,
    });
    setTaskModalVisible(true);
  };

  const handleDeleteTask = (taskId) => {
    Modal.confirm({
      title: "Delete Task",
      content: "Are you sure you want to delete this task?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const result = await deleteTask(id, taskId);
        if (result.success) {
          message.success("Task deleted successfully");
        } else {
          message.error(result.error || "Failed to delete task");
        }
      },
    });
  };

  const handleSaveTask = async (values) => {
    const taskData = {
      title: values.title,
      description: values.description || "",
      priority: values.priority || "medium",
      status: values.status || "pending",
      dueDate: values.dueDate ? values.dueDate.toISOString() : null,
      notes: values.notes || "",
      assignedTo: values.assignedTo || null,
    };

    let result;
    if (editingTask) {
      result = await updateTask(id, editingTask._id, taskData);
    } else {
      result = await addTask(id, taskData);
    }

    if (result.success) {
      message.success(
        `Task ${editingTask ? "updated" : "created"} successfully`
      );
      setTaskModalVisible(false);
      taskForm.resetFields();
      setEditingTask(null);
    } else {
      // Display validation errors if available
      if (result.details && Array.isArray(result.details)) {
        result.details.forEach((detail) => {
          message.error(detail);
        });
      } else {
        message.error(
          result.error || `Failed to ${editingTask ? "update" : "create"} task`
        );
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "blue",
      contacted: "orange",
      qualified: "green",
      proposal: "purple",
      negotiation: "gold",
      won: "success",
      lost: "error",
      converted: "cyan",
    };
    return colors[status] || "default";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "green",
      medium: "orange",
      high: "red",
      urgent: "error",
    };
    return colors[priority] || "default";
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!currentLead) {
    return (
      <div className="flex justify-center items-center h-64">
        <Empty description="Lead not found" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/leads")}
          className="mb-4"
        >
          Back to Leads
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <Title level={2} className="mb-0">
              {currentLead.firstName} {currentLead.lastName}
            </Title>
            <Text className="text-gray-600 dark:text-gray-400">
              {currentLead.jobTitle}{" "}
              {currentLead.company && `at ${currentLead.company}`}
            </Text>
          </div>
          <Space>
            {user?.role === "tenant_admin" && (
              <div className="flex items-center gap-2">
                {currentLead.assignedTo ? (
                  <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 rounded border">
                    <Avatar size="small">
                      {(() => {
                        const assignedToId =
                          currentLead.assignedTo?._id || currentLead.assignedTo;
                        const member = teamMembers.find(
                          (m) => String(m.id) === String(assignedToId)
                        );
                        return (
                          member?.name?.charAt(0)?.toUpperCase() ||
                          currentLead.assignedTo?.firstName
                            ?.charAt(0)
                            ?.toUpperCase() ||
                          "U"
                        );
                      })()}
                    </Avatar>
                    <Text className="text-sm">
                      {(() => {
                        const assignedToId =
                          currentLead.assignedTo?._id || currentLead.assignedTo;
                        const member = teamMembers.find(
                          (m) => String(m.id) === String(assignedToId)
                        );
                        return (
                          member?.name ||
                          (currentLead.assignedTo?.firstName &&
                          currentLead.assignedTo?.lastName
                            ? `${currentLead.assignedTo.firstName} ${currentLead.assignedTo.lastName}`
                            : "Unknown User")
                        );
                      })()}
                    </Text>
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        const assignedToId =
                          currentLead?.assignedTo?._id ||
                          currentLead?.assignedTo;
                        assignForm.setFieldsValue({ assignedTo: assignedToId });
                        setAssignLeadModalVisible(true);
                      }}
                      className="focus-visible:focus"
                      disabled={isConverted}
                    />
                  </div>
                ) : (
                  <Button
                    type="default"
                    icon={<UserOutlined />}
                    onClick={() => {
                      assignForm.setFieldsValue({ assignedTo: null });
                      setAssignLeadModalVisible(true);
                    }}
                    className="focus-visible:focus"
                    disabled={isConverted}
                  >
                    Assign Lead
                  </Button>
                )}
              </div>
            )}
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/leads/${id}/edit`)}
              className="focus-visible:focus"
              disabled={isConverted}
            >
              Edit Lead
            </Button>
            <Button
              type="primary"
              danger
              icon={<CheckCircleOutlined />}
              onClick={() => setConvertToDealModalVisible(true)}
              className="focus-visible:focus"
              disabled={isConverted}
            >
              Convert to Deal
            </Button>
          </Space>
        </div>
      </div>

      {/* 3-Column Layout */}
      <Row gutter={24} className="h-full">
        {/* Column 1: Lead Details (22%) */}
        <Col span={5}>
          <Card className="shadow-soft h-fit bg-white dark:bg-gray-800">
            <div className="text-center mb-4">
              <Avatar
                size={80}
                src={currentLead.profileImageUrl}
                icon={!currentLead.profileImageUrl ? <UserOutlined /> : undefined}
                className="mb-3"
              />
              <Title level={4} className="mb-1">
                {currentLead.firstName} {currentLead.lastName}
              </Title>
              <Text className="text-gray-600 dark:text-gray-400 block mb-3">
                {currentLead.jobTitle}
              </Text>
              <Space>
                <Tag color={getStatusColor(currentLead.status)}>
                  {currentLead.status?.toUpperCase()}
                </Tag>
                <Tag color={getPriorityColor(currentLead.priority)}>
                  {currentLead.priority?.toUpperCase()}
                </Tag>
              </Space>
            </div>

            <Divider />

            <div className="space-y-3">
              <div className="mb-3">
                <Text strong className="block mb-1 text-gray-600 dark:text-gray-400">
                  Email:
                </Text>
                <div className="flex items-center">
                  <MailOutlined className="mr-2 text-gray-400" />
                  <a
                    href={`mailto:${currentLead.email}`}
                    className="text-blue-600 break-all"
                  >
                    {currentLead.email}
                  </a>
                </div>
              </div>

              {currentLead.phone && (
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Phone:
                  </Text>
                  <div className="flex items-center">
                    <PhoneOutlined className="mr-2 text-gray-400 icon-flip-horizontal " />
                    <a
                      href={`tel:${currentLead.phone}`}
                      className="text-blue-600"
                    >
                      {currentLead.phone}
                    </a>
                  </div>
                </div>
              )}

              {currentLead.company && (
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Company:
                  </Text>
                  <Text className="break-words">{currentLead.company}</Text>
                </div>
              )}

              {currentLead.source && (
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Source:
                  </Text>
                  <Text>
                    {currentLead.source.replace("_", " ").toUpperCase()}
                  </Text>
                </div>
              )}

              {currentLead.estimatedValue && (
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Est. Value:
                  </Text>
                  <Text>${currentLead.estimatedValue.toLocaleString()}</Text>
                </div>
              )}

              {currentLead.expectedCloseDate && (
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Expected:
                  </Text>
                  <Text>
                    {dayjs(currentLead.expectedCloseDate).format(
                      "MMM DD, YYYY"
                    )}
                  </Text>
                </div>
              )}

              {user?.role === "tenant_admin" && currentLead.assignedTo && (
                <div className="flex items-center">
                  <Text
                    strong
                    className="w-20 text-gray-600 dark:text-gray-400"
                  >
                    Assigned:
                  </Text>
                  <Text>
                    {(() => {
                      const assignedToId =
                        currentLead.assignedTo?._id || currentLead.assignedTo;
                      const member = teamMembers.find(
                        (m) => String(m.id) === String(assignedToId)
                      );
                      return (
                        member?.name ||
                        (currentLead.assignedTo?.firstName &&
                        currentLead.assignedTo?.lastName
                          ? `${currentLead.assignedTo.firstName} ${currentLead.assignedTo.lastName}`
                          : "Unknown User")
                      );
                    })()}
                  </Text>
                </div>
              )}

              <div className="flex items-center">
                <Text strong className="w-20 text-gray-600 dark:text-gray-400">
                  Created:
                </Text>
                <Text>
                  {dayjs(currentLead.createdAt).format("MMM DD, YYYY")}
                </Text>
              </div>
            </div>

            <Divider />

            <div className="space-y-2">
                            <Button
                              type="primary"
                              icon={<PhoneFilled className="icon-flip-horizontal" />}
                              style={{
                                background: activeCallUUID ? "#ff4d4f" : "#52c41a",
                                borderColor: activeCallUUID ? "#ff4d4f" : "#52c41a",
                                width: "100%"
                              }}
                              onClick={activeCallUUID ? handleEndCall : handleStartManualCall}
                              loading={isCalling && !activeCallUUID}
                            >
                              {isCalling && !activeCallUUID ? "Starting Call..." : activeCallUUID ? "End Call" : "Start Call"}
                            </Button>
              {/* <Button
                icon={<PhoneOutlined />}
                block
                onClick={() => {
                  manualCallForm.setFieldsValue({ to: currentLead.phone });
                  setManualCallModalVisible(true);
                }}
                className="focus-visible:focus"
                disabled={isConverted || !currentLead?.phone}
              >
                Manual Call
              </Button> */}
              <Button
                icon={<EditOutlined />}
                block
                onClick={() => navigate(`/leads/${currentLead._id}/edit`)}
                className="focus-visible:focus"
                disabled={isConverted}
              >
                Edit Lead
              </Button>
            </div>
          </Card>
        </Col>

        {/* Column 2: Tabs Content (60%) */}
        <Col span={14}>
          <Card className="shadow-soft h-full bg-white dark:bg-gray-800">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: "requirements",
                  label: "Requirements",
                  children: (
                    <div
                      className="space-y-4"
                      style={{
                        maxHeight: "550px",
                        overflowY: "auto",
                        minHeight: "200px",
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <Title level={5} className="mb-0">
                          Requirements
                        </Title>
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => setRequirementModalVisible(true)}
                          className="focus-visible:focus"
                        >
                          Add Requirement
                        </Button>
                      </div>
                      <List
                        dataSource={currentLead?.requirements || []}
                        locale={{ emptyText: "No requirements added" }}
                        renderItem={(requirement) => (
                          <List.Item
                            actions={[
                              <Button
                                key="edit"
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() =>
                                  handleEditItem("requirement", requirement)
                                }
                              />,
                              <Button
                                key="delete"
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  handleDeleteItem(
                                    "requirement",
                                    requirement._id
                                  )
                                }
                              />,
                            ]}
                          >
                            <List.Item.Meta
                              title={
                                <div className="flex justify-between items-center">
                                  <Text strong>{requirement.title}</Text>
                                  <Tag
                                    color={
                                      requirement.priority === "urgent"
                                        ? "red"
                                        : requirement.priority === "high"
                                        ? "orange"
                                        : requirement.priority === "medium"
                                        ? "blue"
                                        : "green"
                                    }
                                  >
                                    {requirement.priority?.toUpperCase()}
                                  </Tag>
                                </div>
                              }
                              description={
                                <div>
                                  <Paragraph className="mb-2">
                                    {requirement.description ||
                                      " No description"}
                                  </Paragraph>
                                  {requirement.budget && (
                                    <Text type="secondary">
                                      Budget: $
                                      {requirement.budget?.toLocaleString()}
                                    </Text>
                                  )}
                                </div>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  ),
                },
                {
                  key: "meetings",
                  label: "Meetings",
                  children: (
                    <div
                      className="space-y-4"
                      style={{
                        maxHeight: "550px",
                        overflowY: "auto",
                        minHeight: "200px",
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <Title level={5} className="mb-0">
                          Meetings
                        </Title>
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => setMeetingModalVisible(true)}
                          className="focus-visible:focus"
                        >
                          Schedule Meeting
                        </Button>
                      </div>
                      <List
                        dataSource={currentLead?.meetings || []}
                        locale={{ emptyText: "No meetings scheduled" }}
                        renderItem={(meeting) => (
                          <List.Item
                            actions={[
                              <Button
                                key="edit"
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() =>
                                  handleEditItem("meeting", meeting)
                                }
                              />,
                              <Button
                                key="delete"
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  handleDeleteItem("meeting", meeting._id)
                                }
                              />,
                            ]}
                          >
                            <List.Item.Meta
                              avatar={<Avatar icon={<CalendarOutlined />} />}
                              title={meeting.title}
                              description={
                                <div>
                                  <Text>{meeting.description}</Text>
                                  <br />
                                  <Text type="secondary">
                                    {dayjs(meeting.scheduledAt).format(
                                      "MMM DD, YYYY HH:mm"
                                    )}
                                  </Text>
                                  {meeting.location && (
                                    <>
                                      <br />
                                      <Text type="secondary">
                                        Location: {meeting.location}
                                      </Text>
                                    </>
                                  )}
                                </div>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  ),
                },
                {
                  key: "calllogs",
                  label: (
                    <span>
                      <PhoneFilled /> Call Logs
                    </span>
                  ),
                  children: (
                    <div style={{ maxHeight: "550px", overflowY: "auto", minHeight: "200px" }}>
                      <div className="mb-4">
                        <Title level={5}>Call History</Title>
                      </div>
                      {isLoadingCallLogs ? (
                        <div className="flex justify-center items-center h-32">
                          <Spin />
                        </div>
                      ) : callLogs.length > 0 ? (
                        <div className="space-y-4">
                          {callLogs.map((log) => {
                            // Hide SIP addresses and provider internals from list
                            const showFrom = log.from && !/^sip:/i.test(log.from) && !/phone\.plivo/i.test(log.from);
                            // Hide raw numeric 'to' values (like 916200502473)
                            const showTo = log.to && !/^\+?\d+$/.test(String(log.to));
                            // Hide generic hangup reason 'NORMAL_CLEARING'
                            const showHangup = log.hangupCause && String(log.hangupCause).toUpperCase() !== 'NORMAL_CLEARING';

                            return (
                              <Card 
                                key={log.callUUID} 
                                size="small"
                                className="shadow-sm"
                                bodyStyle={{ padding: '16px' }}
                              >
                                <div className="flex items-start gap-4">
                                  {/* Left Column: Icon */}
                                  <div className="flex-shrink-0">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50">
                                      {log.direction === 'inbound' ? (
                                        <PhoneFilled style={{ color: '#1890ff', transform: 'rotate(90deg)', fontSize: '18px' }} />
                                      ) : (
                                        <PhoneFilled style={{ color: '#52c41a', transform: 'rotate(-90deg)', fontSize: '18px' }} />
                                      )}
                                    </div>
                                  </div>

                                  {/* Middle Column: Call Details */}
                                  <div className="flex-grow min-w-0">
                                    {/* Header Row */}
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <Text strong>
                                          {log.direction === 'inbound' ? 'Outgoinng' : 'Incoming'} Call
                                        </Text>
                                        {log.status && (
                                          <Tag color={log.status === 'completed' ? 'success' : 'warning'}>
                                            {log.status}
                                          </Tag>
                                        )}
                                      </div>
                                      <Text type="secondary" className="text-sm">
                                        {dayjs(log.startTime).format('MMM D, YYYY h:mm A')}
                                      </Text>
                                    </div>

                                    {/* Duration and Cost */}
                                    <div className="flex gap-4 mb-2 text-gray-600">
                                      <span>Duration: {log.duration}s</span>
                                      {log.billDuration && (
                                        <span>Cost: ${log.totalCost}</span>
                                      )}
                                    </div>

                                    {/* From/To Details */}
                                    {showFrom && (
                                      <div className="text-sm text-gray-700">
                                        From: <Text strong>{log.from}</Text>
                                      </div>
                                    )}
                                    {showTo && (
                                      <div className="text-sm text-gray-600 mt-1">
                                        To: <Text type="secondary">{log.to}</Text>
                                      </div>
                                    )}
                                    {showHangup && (
                                      <div className="text-sm text-gray-500 mt-1">
                                        Hangup: <Text type="secondary">{log.hangupCause}</Text>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Card>
                            );
                          })}
                        </div>
                      ) : (
                        <Empty description="No call logs found" />
                      )}
                    </div>
                  ),
                },
                {
                  key: "notes",
                  label: "Notes",
                  children: (
                    <div
                      className="space-y-4"
                      style={{
                        maxHeight: "550px",
                        overflowY: "auto",
                        minHeight: "200px",
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <Title level={5} className="mb-0">
                          Notes
                        </Title>
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setEditingItem(null);
                            noteForm.resetFields();
                            setNoteModalVisible(true);
                          }}
                          className="focus-visible:focus"
                        >
                          Add Note
                        </Button>
                      </div>
                      <List
                        dataSource={currentLead?.notes || []}
                        locale={{ emptyText: "No notes added" }}
                        renderItem={(note) => (
                          <List.Item
                            actions={[
                              <Button
                                key="edit"
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleEditItem("note", note)}
                              />,
                              <Button
                                key="delete"
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  handleDeleteItem("note", note._id)
                                }
                              />,
                            ]}
                          >
                            <List.Item.Meta
                              avatar={<Avatar icon={<FileTextOutlined />} />}
                              title={
                                <div className="flex justify-between">
                                  <Text strong>Note</Text>
                                  <Text type="secondary" className="text-sm">
                                    {dayjs(note.createdAt).format(
                                      "MMM DD, YYYY HH:mm"
                                    )}
                                  </Text>
                                </div>
                              }
                              description={
                                <Paragraph>{note.content}</Paragraph>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  ),
                },
                {
                  key: "documents",
                  label: "Documents",
                  children: (
                    <div
                      className="space-y-4"
                      style={{
                        maxHeight: "550px",
                        overflowY: "auto",
                        minHeight: "200px",
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <Title level={5} className="mb-0">
                          Documents
                        </Title>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => setDocumentModalVisible(true)}
                          size="small"
                        >
                          Upload Document
                        </Button>
                      </div>
                      <Upload.Dragger
                        name="document"
                        multiple
                        customRequest={async ({ file, onSuccess, onError, onProgress }) => {
                          const fileId = `${file.uid}-${Date.now()}`;

                          // Add file to uploading list
                          setUploadingFiles(prev => [...prev, {
                            id: fileId,
                            name: file.name,
                            percent: 0
                          }]);

                          try {
                            const formData = new FormData();
                            formData.append('document', file);

                            const response = await apiService.post(
                              `/leads/${id}/documents/upload`,
                              formData,
                              {
                                headers: {
                                  'Content-Type': 'multipart/form-data',
                                },
                                onUploadProgress: (progressEvent) => {
                                  const percent = Math.round(
                                    (progressEvent.loaded * 100) / progressEvent.total
                                  );
                                  onProgress({ percent });

                                  // Update progress in state
                                  setUploadingFiles(prev =>
                                    prev.map(f =>
                                      f.id === fileId ? { ...f, percent } : f
                                    )
                                  );
                                },
                              }
                            );

                            onSuccess(response.data);
                            message.success(`${file.name} uploaded successfully`);

                            // Remove from uploading list
                            setUploadingFiles(prev => prev.filter(f => f.id !== fileId));

                            // Refresh lead data to show new document
                            await fetchLeadById(id);
                          } catch (error) {
                            console.error('Upload error:', error);
                            onError(error);

                            // Remove from uploading list
                            setUploadingFiles(prev => prev.filter(f => f.id !== fileId));

                            message.error(
                              error.response?.data?.message ||
                              `${file.name} upload failed`
                            );
                          }
                        }}
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                        showUploadList={false}
                        disabled={uploadingFiles.length > 0}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                          Click or drag file to this area to upload
                        </p>
                        <p className="ant-upload-hint">
                          Supports: Images, PDF, Word, Excel, PowerPoint, Text, CSV (Max 10MB)
                        </p>
                      </Upload.Dragger>

                      {uploadingFiles.length > 0 && (
                        <div style={{ marginTop: '16px' }}>
                          {uploadingFiles.map(file => (
                            <div key={file.id} style={{ marginBottom: '8px' }}>
                              <Text>{file.name}</Text>
                              <Progress percent={file.percent} status="active" />
                            </div>
                          ))}
                        </div>
                      )}

                      <List
                        dataSource={currentLead.documents || []}
                        locale={{ emptyText: "No documents uploaded" }}
                        renderItem={(doc) => (
                          <List.Item
                            actions={[
                              <Button
                                type="link"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownloadDocument(doc)}
                              >
                                Download
                              </Button>,
                            ]}
                          >
                            <List.Item.Meta
                              avatar={<Avatar icon={<FileOutlined />} />}
                              title={doc.name}
                              description={`${doc.size ? `${Math.round(doc.size / 1024)} KB` : 'Unknown size'} • ${doc.uploadedAt ? dayjs(doc.uploadedAt).format("MMM DD, YYYY") : dayjs(doc.createdAt).format("MMM DD, YYYY")}`}
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  ),
                },
                {
                  key: "tasks",
                  label: "Tasks",
                  children: (
                    <div style={{ padding: "20px" }}>
                      <div
                        style={{
                          marginBottom: "16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <h4>Tasks ({currentLead?.tasks?.length || 0})</h4>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          size="small"
                          onClick={handleAddTask}
                        >
                          Add Task
                        </Button>
                      </div>

                      {!currentLead?.tasks || currentLead.tasks.length === 0 ? (
                        <Empty
                          description="No tasks found"
                          style={{ margin: "40px 0" }}
                        />
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            maxHeight: "340px" /* Adjusted for padding */,
                            overflowY: "auto",
                            minHeight: "140px" /* Adjusted for padding */,
                          }}
                        >
                          {currentLead.tasks.map((task, index) => (
                            <Card
                              key={task._id || index}
                              size="small"
                              style={{ border: "1px solid #f0f0f0" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontWeight: "bold",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    {task.title}
                                  </div>
                                  {task.description && (
                                    <div
                                      style={{
                                        color: "#666",
                                        marginBottom: "8px",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {task.description}
                                    </div>
                                  )}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Tag
                                      color={
                                        task.priority === "high"
                                          ? "red"
                                          : task.priority === "medium"
                                          ? "orange"
                                          : "blue"
                                      }
                                    >
                                      {task.priority?.toUpperCase() || "MEDIUM"}
                                    </Tag>
                                    <Tag
                                      color={
                                        task.status === "completed"
                                          ? "green"
                                          : task.status === "in_progress"
                                          ? "blue"
                                          : "orange"
                                      }
                                    >
                                      {task.status
                                        ?.replace("_", " ")
                                        .toUpperCase() || "PENDING"}
                                    </Tag>
                                    {task.dueDate && (
                                      <Tag color="purple">
                                        Due:{" "}
                                        {new Date(
                                          task.dueDate
                                        ).toLocaleDateString()}
                                      </Tag>
                                    )}
                                  </div>
                                </div>
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <Button
                                    size="small"
                                    type="link"
                                    onClick={() => handleEditTask(task)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    type="link"
                                    danger
                                    onClick={() => handleDeleteTask(task._id)}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  ),
                },
                ...(isWhatsAppEnabled ? [{
                  key: "whatsapp",
                  label: (
                    <span>
                      <WhatsAppOutlined /> WhatsApp
                    </span>
                  ),
                  children: (
                    <div
                      style={{
                        height: "550px",
                        display: "flex",
                        flexDirection: "column",
                        background: "#f0f2f5",
                      }}
                    >
                      {/* Chat Header */}
                      <div
                        style={{
                          padding: "16px",
                          background: "#25D366",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <Avatar
                          size={40}
                          style={{ background: "#128C7E" }}
                          icon={<UserOutlined />}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                            {currentLead.firstName} {currentLead.lastName}
                          </div>
                          <div style={{ fontSize: "12px", opacity: 0.9 }}>
                            {currentLead.phone || "No phone number"}
                          </div>
                        </div>
                        <Tag color="success">Active</Tag>
                      </div>

                      {whatsappUnavailable && (
                        <div style={{ padding: "12px 16px" }}>
                          <Alert
                            type="warning"
                            showIcon
                            message="WhatsApp integration is unavailable"
                            description="Please verify the WhatsApp configuration or try again later."
                          />
                        </div>
                      )}

                      {/* Chat Messages Area */}
                      <div
                        ref={chatContainerRef}
                        style={{
                          flex: 1,
                          overflowY: "auto",
                          padding: "16px",
                          backgroundImage:
                            "url('data:image/svg+xml,%3Csvg width=\"100\" height=\"100\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cpath d=\"M0 0h100v100H0z\" fill=\"%23e5ddd5\"/%3E%3Cpath d=\"M25 25l5 5-5 5-5-5z\" fill=\"%23d1d7db\" opacity=\".1\"/%3E%3C/svg%3E')",
                        }}
                      >
                        {isChatLoading ? (
                          <div className="flex justify-center items-center h-full">
                            <Spin size="large" />
                          </div>
                        ) : chatMessages.length === 0 ? (
                          <Empty
                            description="No messages yet"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{ marginTop: "100px" }}
                          />
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {chatMessages.map((msg) => (
                              <div
                                key={msg._id}
                                style={{
                                  display: "flex",
                                  justifyContent:
                                    msg.entity === "user" ? "flex-start" : "flex-end",
                                }}
                              >
                                <div
                                  style={{
                                    maxWidth: "70%",
                                    background: msg.entity === "user" ? "white" : "#dcf8c6",
                                    padding: "8px 12px",
                                    borderRadius: "8px",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                                    {msg.message}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#667781",
                                      textAlign: "right",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-end",
                                      gap: "4px",
                                    }}
                                  >
                                    {dayjs(msg.timestamp).format("HH:mm A")}
                                    {msg.entity === "bot" && (
                                      <CheckCircleOutlined style={{ fontSize: "12px", color: "#53bdeb" }} />
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Message Input Area */}
                      <div
                        style={{
                          padding: "12px 16px",
                          background: "#f0f2f5",
                          borderTop: "1px solid #e0e0e0",
                        }}
                      >
                        <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                          <Input.TextArea
                            placeholder="Type a message..."
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            style={{
                              flex: 1,
                              borderRadius: "20px",
                              padding: "8px 16px",
                            }}
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            onPressEnter={(e) => {
                              if (!e.shiftKey) {
                                e.preventDefault();
                                handleSendChatMessage();
                              }
                            }}
                            disabled={whatsappUnavailable}
                          />
                          <Button
                            type="primary"
                            shape="circle"
                            icon={<SendOutlined />}
                            size="large"
                            style={{
                              background: "#25D366",
                              borderColor: "#25D366",
                            }}
                            onClick={handleSendChatMessage}
                            disabled={whatsappUnavailable}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#667781",
                            marginTop: "8px",
                            textAlign: "center",
                          }}
                        >
                          {whatsappUnavailable
                            ? "WhatsApp messaging is currently unavailable"
                            : "Press Enter to send • Shift+Enter for new line"}
                        </div>
                      </div>
                    </div>
                  ),
                }] : []),
                // Conditionally add AI Calling tab based on environment variable
                ...(isAICallingEnabled ? [{
                  key: "aicalling",
                  label: (
                    <span>
                      <CustomerServiceOutlined /> AI Calling
                    </span>
                  ),
                  children: (
                    <div
                      style={{
                        height: "550px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Call Header */}
                      <div
                        style={{
                          padding: "16px 20px",
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                          <Avatar
                            size={48}
                            style={{ 
                              background: "rgba(255, 255, 255, 0.2)",
                              border: "2px solid white"
                            }}
                            icon={<CustomerServiceOutlined style={{ fontSize: "24px" }} />}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "2px" }}>
                              {currentLead.firstName} {currentLead.lastName}
                            </div>
                            <div style={{ fontSize: "12px", opacity: 0.9 }}>
                              {currentLead.phone || "No phone number"}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                              type="primary"
                              size="small"
                              icon={<PhoneFilled className="icon-flip-horizontal" />}
                              style={{
                                background: "#52c41a",
                                borderColor: "#52c41a",
                              }}
                              onClick={handleStartAICallCampaign}
                              loading={isCalling}
                              disabled={isCalling}
                            >
                              {isCalling ? "Starting Call..." : "Start Call"}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Single Column Layout for Call Transcript */}
                          <div
                            style={{
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              background: "#fafafa",
                            }}
                          >
                            <div
                              style={{
                                padding: "12px 16px",
                                background: "white",
                                borderBottom: "1px solid #f0f0f0",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              <FileTextOutlined style={{ marginRight: "8px" }} />
                              Call Transcript
                            </div>
                            <div
                              style={{
                                flex: 1,
                                overflowY: "auto",
                                padding: "16px",
                              }}
                            >
                              {isTranscriptsLoading ? (
                                <div className="flex justify-center items-center h-full">
                                  <Spin size="large" />
                                </div>
                              ) : transcripts.length === 0 ? (
                                <Empty
                                  description="No call transcripts available for this lead."
                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                  style={{ marginTop: "100px" }}
                                />
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                                  {transcripts.map((call, callIndex) => (
                                    <div key={call.id || callIndex} style={{ border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", background: "white" }}>
                                      <div style={{ marginBottom: "12px", paddingBottom: "8px", borderBottom: "1px solid #f0f0f0" }}>
                                        <Title level={5} style={{ margin: 0 }}>Call ID: {call.id}</Title>
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                          Status: <Tag color={call.status === "completed" ? "green" : "orange"}>{call.status?.toUpperCase()}</Tag>
                                          Duration: {call.duration} seconds
                                          <br />
                                          Start: {dayjs(call.callStartDate).format("MMM DD, YYYY HH:mm:ss")}
                                          <br />
                                          End: {dayjs(call.callEndDate).format("MMM DD, YYYY HH:mm:ss")}
                                        </Text>
                                      </div>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                        {call.transcript.map((entry, entryIndex) => (
                                          <div
                                            key={entryIndex}
                                            style={{
                                              display: "flex",
                                              justifyContent: entry.role === "assistant" ? "flex-end" : "flex-start",
                                            }}
                                          >
                                            <div
                                              style={{
                                                maxWidth: "70%", // Limit width to prevent messages from spanning full width
                                                background: entry.role === "assistant" ? "#e6f7ff" : "#f5f5f5",
                                                padding: "10px 12px",
                                                borderRadius: "8px",
                                                borderLeft: entry.role === "assistant" ? "3px solid #1890ff" : "3px solid #d9d9d9",
                                                alignSelf: entry.role === "assistant" ? "flex-end" : "flex-start",
                                              }}
                                            >
                                              <div style={{ fontSize: "11px", color: "#999", marginBottom: "4px", textAlign: entry.role === "assistant" ? "right" : "left" }}>
                                                {entry.role === "assistant" ? "AI Assistant" : currentLead.firstName}
                                              </div>
                                              <div>
                                                {entry.content}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                    </div>
                  ),
                }] : []),
              ]}
            />
          </Card>
        </Col>

        {/* Column 3: Timeline (18%) */}
        <Col span={5}>
          <Card
            className="shadow-soft h-full bg-white dark:bg-gray-800"
            title="Activity Timeline"
          >
            <Timeline
              items={[
                {
                  dot: <CheckCircleOutlined className="text-green-500" />,
                  children: (
                    <div>
                      <Text strong>Lead Created</Text>
                      <br />
                      <Text type="secondary" className="text-sm">
                        {dayjs(currentLead.createdAt).format(
                          "MMM DD, YYYY HH:mm"
                        )}
                      </Text>
                      <br />
                      <Text className="text-sm">
                        Lead was added to the system
                      </Text>
                    </div>
                  ),
                },
                ...(currentLead.activities || []).map((activity, index) => ({
                  dot:
                    activity.type === "call" ? (
                      <PhoneOutlined className="text-blue-500 icon-flip-horizontal" />
                    ) : activity.type === "email" ? (
                      <MailOutlined className="text-purple-500" />
                    ) : activity.type === "meeting" ? (
                      <CalendarOutlined className="text-orange-500" />
                    ) : (
                      <ClockCircleOutlined className="text-gray-500" />
                    ),
                  children: (
                    <div key={index}>
                      <Text strong>{activity.subject}</Text>
                      <br />
                      <Text type="secondary" className="text-sm">
                        {dayjs(activity.createdAt).format("MMM DD, YYYY HH:mm")}
                      </Text>
                      <br />
                      <Text className="text-sm">{activity.description}</Text>
                    </div>
                  ),
                })),
                {
                  dot: (
                    <ExclamationCircleOutlined className="text-orange-500" />
                  ),
                  children: (
                    <div>
                      <Text strong>Follow-up Required</Text>
                      <br />
                      <Text type="secondary" className="text-sm">
                        {currentLead.expectedCloseDate
                          ? dayjs(currentLead.expectedCloseDate).format(
                              "MMM DD, YYYY"
                            )
                          : "No date set"}
                      </Text>
                      <br />
                      <Text className="text-sm">Next action needed</Text>
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Add Note Modal */}
      <Modal
        title={editingItem?.type === "note" ? "Edit Note" : "Add Note"}
        open={noteModalVisible}
        onCancel={() => {
          setNoteModalVisible(false);
          setEditingItem(null);
          noteForm.resetFields();
        }}
        footer={null}
      >
        <Form form={noteForm} onFinish={handleUpdateNote} layout="vertical">
          <Form.Item
            name="content"
            label="Note Content"
            rules={[
              { required: true, message: "Please enter notes" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea rows={4} placeholder="Enter your note here..." />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setNoteModalVisible(false);
                  setEditingItem(null);
                  noteForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem?.type === "note" ? "Update Note" : "Add Note"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Activity Modal */}
      <Modal
        title="Add Activity"
        open={activityModalVisible}
        onCancel={() => setActivityModalVisible(false)}
        footer={null}
      >
        <Form
          form={activityForm}
          onFinish={handleAddActivity}
          layout="vertical"
        >
          <Form.Item
            name="type"
            label="Activity Type"
            rules={[{ required: true, message: "Please select activity type" }]}
          >
            <Select onChange={setActivityType}>
              <Option value="call">Call</Option>
              <Option value="email">Email</Option>
              <Option value="meeting">Meeting</Option>
              <Option value="task">Task</Option>
              <Option value="note">Note</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: "Please enter subject" }]}
          >
            <Input placeholder="Activity subject" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Activity description" />
          </Form.Item>
          <Form.Item name="scheduledAt" label="Scheduled Date">
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => setActivityModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add Activity
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Requirement Modal */}
      {/* <Modal
        title={
          editingItem?.type === "requirement"
            ? "Edit Requirement"
            : "Add Requirement"
        }
        open={requirementModalVisible}
        onCancel={() => {
          setRequirementModalVisible(false);
          setEditingItem(null);
          requirementForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={requirementForm}
          onFinish={handleAddRequirement}
          layout="vertical"
        >
          <Form.Item
            name="title"
            label="Requirement Title"
            rules={[
              { required: true, message: "Please enter requirement title" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter requirement title" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
           rules={[
              { required: true, message: "Please enter requirement title" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Describe the requirement in detail"
            />
          </Form.Item>
          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: "Please select priority" }]}
          >
            <Select placeholder="Select priority">
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
              <Option value="urgent">Urgent</Option>
            </Select>
          </Form.Item>
          <Form.Item name="budget" label="Budget Range">
            <Input placeholder="e.g., $10,000 - $50,000" />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setRequirementModalVisible(false);
                  setEditingItem(null);
                  requirementForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem?.type === "requirement" ? "Update" : "Add"}{" "}
                Requirement
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal> */}

      {/* Meeting Modal */}
      {/* <Modal
        title={
          editingItem?.type === "meeting" ? "Edit Meeting" : "Schedule Meeting"
        }
        open={meetingModalVisible}
        onCancel={() => {
          setMeetingModalVisible(false);
          setEditingItem(null);
          meetingForm.resetFields();
        }}
        footer={null}
      >
        <Form form={meetingForm} onFinish={handleAddMeeting} layout="vertical">
          <Form.Item
            name="title"
            label="Meeting Title"
            rules={[{ required: true, message: "Please enter meeting title" }]}
          >
            <Input placeholder="Enter meeting title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Meeting agenda or description" />
          </Form.Item>
          <Form.Item
            name="scheduledAt"
            label="Date & Time"
            rules={[{ required: true, message: "Please select date and time" }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              className="w-full"
              placeholder="Select date and time"
            />
          </Form.Item>
          <Form.Item name="duration" label="Duration (minutes)">
            <InputNumber
              min={15}
              max={480}
              placeholder="60"
              className="w-full"
            />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input placeholder="Meeting location or video link" />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setMeetingModalVisible(false);
                  setEditingItem(null);
                  meetingForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem?.type === "meeting" ? "Update" : "Schedule"}{" "}
                Meeting
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal> */}

      {/* Call Modal */}
      {/* <Modal
        title={editingItem?.type === "call" ? "Edit Call" : "Log Call"}
        open={callModalVisible}
        onCancel={() => {
          setCallModalVisible(false);
          setEditingItem(null);
          callForm.resetFields();
        }}
        footer={null}
      >
        <Form form={callForm} onFinish={handleAddCall} layout="vertical">
          <Form.Item
            name="type"
            label="Call Type"
            rules={[{ required: true, message: "Please select call type" }]}
          >
            <Select placeholder="Select call type">
              <Option value="inbound">Inbound</Option>
              <Option value="outbound">Outbound</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="date"
            label="Call Date & Time"
            rules={[
              { required: true, message: "Please select call date and time" },
            ]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              className="w-full"
              placeholder="Select call date and time"
            />
          </Form.Item>
          <Form.Item name="duration" label="Duration (minutes)">
            <InputNumber
              min={1}
              max={300}
              placeholder="15"
              className="w-full"
            />
          </Form.Item>
          <Form.Item name="outcome" label="Call Outcome">
            <Select placeholder="Select outcome">
              <Option value="successful">Successful</Option>
              <Option value="voicemail">Voicemail</Option>
              <Option value="no_answer">No Answer</Option>
              <Option value="busy">Busy</Option>
              <Option value="follow_up_required">Follow-up Scheduled</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="notes"
            label="Call Notes"
            rules={[{ required: true, message: "Please enter call notes" }]}
          >
            <TextArea
              rows={4}
              placeholder="What was discussed during the call?"
            />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setCallModalVisible(false);
                  setEditingItem(null);
                  callForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem?.type === "call" ? "Update" : "Log"} Call
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal> */}

      {/* Document Upload Modal */}
      <Modal
        title="Upload Document"
        open={documentModalVisible}
        onCancel={() => setDocumentModalVisible(false)}
        footer={null}
      >
        <div className="space-y-4">
          <Upload.Dragger
            name="files"
            multiple
            action="/api/upload"
            onChange={(info) => {
              if (info.file.status === "done") {
                message.success(`${info.file.name} file uploaded successfully`);
                setDocumentModalVisible(false);
              } else if (info.file.status === "error") {
                message.error(`${info.file.name} file upload failed.`);
              }
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for single or bulk upload. Documents will be attached to
              this lead.
            </p>
          </Upload.Dragger>
        </div>
      </Modal>

      {/* Requirement Modal */}
      <Modal
        title={editingItem ? "Edit Requirement" : "Add Requirement"}
        open={requirementModalVisible}
        onCancel={() => {
          setRequirementModalVisible(false);
          requirementForm.resetFields();
          setEditingItem(null);
        }}
        footer={null}
      >
        <Form
          form={requirementForm}
          onFinish={
            editingItem ? handleUpdateRequirement : handleAddRequirement
          }
          layout="vertical"
        >
          <Form.Item
            name="title"
            label="Requirement title"
            rules={[
              { required: true, message: "Please enter requirement title" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter requirement title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter requirement description" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue="medium">
            <Select>
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
              <Option value="critical">Critical</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="pending">
            <Select>
              <Option value="pending">Pending</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Form.Item>
          <Form.Item name="budget" label="Budget ($)">
            <InputNumber
              min={0}
              className="w-full"
              placeholder="Enter budget"
            />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setRequirementModalVisible(false);
                  requirementForm.resetFields();
                  setEditingItem(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem ? "Update" : "Add"} Requirement
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Meeting Modal */}
      <Modal
        title={editingItem ? "Edit Meeting" : "Schedule Meeting"}
        open={meetingModalVisible}
        onCancel={() => {
          setMeetingModalVisible(false);
          meetingForm.resetFields();
          setEditingItem(null);
        }}
        footer={null}
      >
        <Form
          form={meetingForm}
          onFinish={editingItem ? handleUpdateMeeting : handleAddMeeting}
          layout="vertical"
        >
          <Form.Item
            name="title"
            label="Meeting Title"
            rules={[
              { required: true, message: "Please enter meeting title" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter meeting title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter meeting description" />
          </Form.Item>
          <Form.Item
            name="scheduledAt"
            label="Scheduled Date & Time"
            rules={[
              {
                required: true,
                message: "Please select meeting date and time",
              },
            ]}
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              className="w-full"
              placeholder="Select date and time"
            />
          </Form.Item>
          <Form.Item name="duration" label="Duration (minutes)">
            <InputNumber
              min={1}
              className="w-full"
              placeholder="Enter duration in minutes"
            />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input placeholder="Enter meeting location" />
          </Form.Item>
          <Form.Item name="attendees" label="Attendees">
            <Input placeholder="Enter attendees (comma separated)" />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="scheduled">
            <Select>
              <Option value="scheduled">Scheduled</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
              <Option value="rescheduled">Rescheduled</Option>
            </Select>
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setMeetingModalVisible(false);
                  meetingForm.resetFields();
                  setEditingItem(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem ? "Update" : "Schedule"} Meeting
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Call Modal */}
      <Modal
        title={editingItem ? "Edit Call" : "Log Call"}
        open={callModalVisible}
        onCancel={() => {
          setCallModalVisible(false);
          callForm.resetFields();
          setEditingItem(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={callForm}
          onFinish={editingItem ? handleUpdateCall : handleAddCall}
          layout="vertical"
          initialValues={{
            type: "outbound",
            outcome: "successful",
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Call Type"
                rules={[{ required: true, message: "Please select call type" }]}
              >
                <Select placeholder="Select call type">
                  <Option value="inbound">📞 Inbound</Option>
                  <Option value="outbound">📱 Outbound</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="outcome"
                label="Call Outcome"
                rules={[
                  { required: true, message: "Please select call outcome" },
                ]}
              >
                <Select placeholder="Select outcome">
                  <Option value="successful">✅ Successful</Option>
                  <Option value="no_answer">❌ No Answer</Option>
                  <Option value="busy">📵 Busy</Option>
                  <Option value="voicemail">📧 Voicemail</Option>
                  <Option value="follow_up_required">
                    🔄 Follow-up Required
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="subject"
            label="Call Subject"
            rules={[
              { required: true, message: "Please enter call subject" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter call subject (e.g., Follow-up on proposal)" />
          </Form.Item>
          <Form.Item
            name="notes"
            label="Call Notes"
            rules={[
              { required: true, message: "Please enter call notes" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea rows={4} placeholder="Enter call notes and details" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="duration" label="Duration (minutes)">
                <InputNumber
                  min={0}
                  className="w-full"
                  placeholder="Enter duration"
                  addonAfter="min"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="scheduledAt" label="Call Date & Time">
                <DatePicker
                  showTime
                  className="w-full"
                  placeholder="Select date & time"
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setCallModalVisible(false);
                  callForm.resetFields();
                  setEditingItem(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem ? "Update" : "Log"} Call
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Document Modal */}
      <Modal
        title="Add Document"
        open={documentModalVisible}
        onCancel={() => setDocumentModalVisible(false)}
        footer={null}
      >
        <Form onFinish={handleAddDocument} layout="vertical">
          <Form.Item
            name="name"
            label="Document Name"
            rules={[{ required: true, message: "Please enter document name" }]}
          >
            <Input placeholder="Enter document name" />
          </Form.Item>
          <Form.Item name="type" label="Document Type" initialValue="other">
            <Select>
              <Option value="contract">Contract</Option>
              <Option value="proposal">Proposal</Option>
              <Option value="invoice">Invoice</Option>
              <Option value="presentation">Presentation</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="url"
            label="Document URL"
            rules={[{ required: true, message: "Please enter document URL" }]}
          >
            <Input placeholder="Enter document URL or file path" />
          </Form.Item>
          <Form.Item name="size" label="File Size (bytes)">
            <InputNumber
              min={0}
              className="w-full"
              placeholder="Enter file size"
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter document description" />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => setDocumentModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add Document
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Convert to Deal Modal */}
      <Modal
        title="Convert Lead to Deal"
        open={convertToDealModalVisible}
        onCancel={() => {
          setConvertToDealModalVisible(false);
          convertForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={convertForm}
          onFinish={handleConvertToDeal}
          layout="vertical"
          initialValues={{
            title: `${currentLead?.firstName} ${currentLead?.lastName} - ${
              currentLead?.company || "Deal"
            }`,
            value: currentLead?.estimatedValue,
            expectedCloseDate: currentLead?.expectedCloseDate
              ? dayjs(currentLead.expectedCloseDate)
              : null,
            stage: "qualification",
          }}
        >
          <Form.Item
            name="title"
            label="Deal Title"
            rules={[{ required: true, message: "Please enter deal title" }]}
          >
            <Input placeholder="Enter deal title" />
          </Form.Item>
          <Form.Item
            name="value"
            label="Deal Value ($)"
            rules={[{ required: true, message: "Please enter deal value" }]}
          >
            <InputNumber
              min={0}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              className="w-full"
              placeholder="Enter deal value"
            />
          </Form.Item>
          <Form.Item
            name="stage"
            label="Deal Stage"
            rules={[{ required: true, message: "Please select deal stage" }]}
          >
            <Select placeholder="Select deal stage">
              <Option value="prospecting">Prospecting</Option>
              <Option value="qualification">Qualification</Option>
              <Option value="needs_analysis">Needs Analysis</Option>
              <Option value="proposal">Proposal</Option>
              <Option value="negotiation">Negotiation</Option>
              <Option value="closed_won">Closed Won</Option>
              <Option value="closed_lost">Closed Lost</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="expectedCloseDate"
            label="Expected Close Date"
            rules={[
              { required: true, message: "Please select expected close date" },
            ]}
          >
            <DatePicker
              className="w-full"
              placeholder="Select expected close date"
            />
          </Form.Item>
          <Form.Item name="probability" label="Win Probability (%)">
            <InputNumber
              min={0}
              max={100}
              className="w-full"
              placeholder="Enter win probability"
            />
          </Form.Item>
          <Form.Item name="notes" label="Conversion Notes">
            <TextArea
              rows={3}
              placeholder="Add any notes about this conversion..."
            />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setConvertToDealModalVisible(false);
                  convertForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Convert to Deal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Task Modal */}
      <Modal
        title={editingTask ? "Edit Task" : "Add Task"}
        open={taskModalVisible}
        onCancel={() => {
          setTaskModalVisible(false);
          taskForm.resetFields();
          setEditingTask(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={taskForm} layout="vertical" onFinish={handleSaveTask}>
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: "Please enter task title" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter task title" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter task description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" initialValue="medium">
                <Select>
                  <Select.Option value="low">Low</Select.Option>
                  <Select.Option value="medium">Medium</Select.Option>
                  <Select.Option value="high">High</Select.Option>
                  <Select.Option value="urgent">Urgent</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status" initialValue="pending">
                <Select>
                  <Select.Option value="pending">Pending</Select.Option>
                  <Select.Option value="in_progress">In Progress</Select.Option>
                  <Select.Option value="completed">Completed</Select.Option>
                  <Select.Option value="cancelled">Cancelled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="dueDate"
            label="Due Date"
            extra="If not selected, defaults to tomorrow"
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="assignedTo" label="Assign To Team Member">
            <Select
              placeholder="Select team member"
              allowClear
              loading={loadingTeamMembers}
              showSearch
              filterOption={(input, option) => {
                const member = teamMembers.find(m => m.id === option.value);
                if (!member) return false;
                const searchText = input.toLowerCase();
                return (
                  member.name.toLowerCase().includes(searchText) ||
                  member.role?.toLowerCase().includes(searchText)
                );
              }}
            >
              {teamMembers.map((member) => (
                <Select.Option key={member.id} value={member.id}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Avatar size="small" src={member.avatar}>
                      {member.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <span>{member.name}</span>
                    <span style={{ color: "#999", fontSize: "12px" }}>
                      ({member.role})
                    </span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Additional notes" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setTaskModalVisible(false);
                  taskForm.resetFields();
                  setEditingTask(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingTask ? "Update" : "Create"} Task
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Lead Modal */}
      <Modal
        title="Change Assignment"
        open={assignLeadModalVisible}
        onCancel={() => {
          setAssignLeadModalVisible(false);
          assignForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={assignForm}
          onFinish={handleAssignLead}
          layout="vertical"
          initialValues={{
            assignedTo: currentLead?.assignedTo?._id || currentLead?.assignedTo,
          }}
        >
          <Form.Item
            name="assignedTo"
            label="Assign to Team Member"
            rules={[{ required: true, message: "Please select a team member" }]}
          >
            <Select
              placeholder="Select team member"
              loading={loadingTeamMembers}
              showSearch
              filterOption={(input, option) => {
                const member = teamMembers.find(m => m.id === option.value);
                if (!member) return false;
                const searchText = input.toLowerCase();
                return (
                  member.name.toLowerCase().includes(searchText) ||
                  member.role?.toLowerCase().includes(searchText)
                );
              }}
            >
              {teamMembers.map((member) => (
                <Option key={member.id} value={member.id}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Avatar size="small" src={member.avatar}>
                      {member.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <span>{member.name}</span>
                    <span style={{ color: "#999", fontSize: "12px" }}>
                      ({member.role})
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setAssignLeadModalVisible(false);
                  assignForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Assignment
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Manual Call Modal */}
      <Modal
        title="Initiate Manual Call"
        open={manualCallModalVisible}
        onCancel={() => {
          setManualCallModalVisible(false);
          manualCallForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={manualCallForm}
          onFinish={handleStartManualCall}
          layout="vertical"
          initialValues={{
            to: currentLead?.phone,
            fromNumber: DEFAULT_FROM_NUMBER,
          }}
        >
          <Form.Item label="Calling To">
            <Input value={currentLead?.phone} disabled />
          </Form.Item>
          <Form.Item
            name="fromNumber"
            label="Your Caller ID (From Number)"
          >
            <Input placeholder="Configured via env" disabled />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setManualCallModalVisible(false);
                  manualCallForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={isCalling}>
                {isCalling ? "Calling..." : "Call Now"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LeadDetailView;
